import React from 'react';
import { orderBy, map, isEmpty, filter } from 'lodash';
import BaseWidget from 'nucleus-widgets/lib/BaseWidget';
import Dialog from 'nucleus-core/dialog/Dialog';
import SpeakerProfileImage from '../ProfileImage/SpeakerProfileImage';
import { resolve } from '@cvent/nucleus-dynamic-css';
import { resolveTestId } from '@cvent/nucleus-test-automation';
import Icon from '@cvent/nucleus-icon';
import InteractiveElement from 'nucleus-core/containers/InteractiveElement';
import { tapOrClick } from 'nucleus-core/touchEventHandlers';
import DialogStyles from 'nucleus-core/less/cv/Dialog.less';
import AnimationUpStyles from 'nucleus-core/less/cv/Dialog.transitionUp.less';
import SpeakersStyles from '../SpeakersStyles.less';
import PropTypes from 'prop-types';
import SpeakerDocuments from '../SpeakerDocuments/SpeakerDocuments';
import SpeakersWidgetBehavior from '../SpeakersWidgetBehavior';

export default class SpeakerBioModal extends BaseWidget {
  static displayName = 'SpeakerBioModal';
  static contextType = SpeakersWidgetBehavior;
  static propTypes = {
    style: PropTypes.any.isRequired,
    classes: PropTypes.any.isRequired,
    translate: PropTypes.func.isRequired,
    speaker: PropTypes.string.isRequired,
    speakerCategory: PropTypes.string,
    displayOptions: PropTypes.array,
    showBioModal: PropTypes.func.isRequired,
    dialogContainerStyle: PropTypes.any.isRequired
  };

  generateSocialMediaIcon(icon, url, ariaLabel) {
    const { classes } = this.props;
    const props = { ...this.props, classes: { ...SpeakersStyles, ...classes } };
    return (
      <a
        {...resolveTestId(props, `-${ icon }`)}
        {...resolve(props, 'speakerContactIcons')}
        aria-label={ariaLabel}
        target="_blank"
        href={url}
      >
        <Icon icon={icon} classes={classes}/>
      </a>
    );
  }

  getVisibleWebsites(websites) {
    return filter(websites, website => website.isDisplay);
  }

  hasWebsites(websites) {
    return !isEmpty(websites);
  }

  renderWebsites(websites) {
    return map(
      orderBy(filter(websites, website => website.isDisplay), website => website.relatedUrlName.toLowerCase()),
      website =>
        (
          <li {...resolve(this.props, 'list')}>
            <a
              {...resolve(this.props, 'speakerModalWebsites')}
              href={website.relatedUrl}
              target="_blank"
            >
              {website.relatedUrlName}
            </a>
          </li>
        )
    );
  }

  componentDidMount() {
    if (this.props.displayOptions.includes('speakerDocuments') && this.props.loadSpeakerDocuments) {
      const speakerId = this.props.speaker.id;
      this.props.loadSpeakerDocuments(speakerId);
    }
  }

  // eslint-disable-next-line complexity
  render() {
    const { speaker, speakerCategory, showBioModal, displayOptions, style,
      classes, translate, dialogContainerStyle, getSpeakerDocuments } = this.props;
    const { id, firstName, lastName, prefix, title, company, designation,
      biography, facebookUrl, linkedInUrl, twitterUrl, websites } = speaker;

    const ModalClasses = Object.assign({}, DialogStyles, {
      ...classes,
      dialogContainer: DialogStyles.dialogContainer,
      ...SpeakersStyles,
      transition: AnimationUpStyles
    });

    const ModalStyle = {
      ...style,
      dragContainer: dialogContainerStyle
    };

    const visibleWebsites = this.getVisibleWebsites(websites);
    const props = { ...this.props, classes: { ...SpeakersStyles, ...classes } };
    return (
      <Dialog
        dialogId="speakerBioModal"
        isOpen={!!id}
        classes={ModalClasses}
        style={ModalStyle}
        isModal
      >
        <div>
          { displayOptions.includes('profileImage') && <div {...resolve(props, 'speakerImageWrapper')}>
            <SpeakerProfileImage
              classes={classes}
              style={style}
              speaker={speaker}
            />
          </div> }
          <div {...resolve(props, 'speakerModalWrapper')}>
            <span {...resolve(props, 'speakerModalName')} {...resolveTestId(props, '-modal-name')} >
              { displayOptions.includes('prefix') && <span>{ prefix } </span> }
              <span>{ translate(firstName) } { translate(lastName) }</span>
              { displayOptions.includes('category') && speakerCategory && <span> - { translate(speakerCategory.name) } </span> }
            </span>
            <div
              {...resolve(props, 'speakerModalDesignation')}
              {...resolveTestId(props, '-modal-designation')}
            >
              { displayOptions.includes('designation') && <span>{ translate(designation) } </span> }
            </div>
            <div {...resolve(props, 'speakerModalTitle')} {...resolveTestId(props, '-modal-title')} >
              { displayOptions.includes('title') && <span>{ translate(title) } </span> }
            </div>
            <div {...resolve(props, 'speakerModalCompany')} {...resolveTestId(props, '-modal-company')} >
              { displayOptions.includes('company') && <span>{ translate(company) } </span> }
            </div>
            <div {...resolveTestId(props, '-modal-contact-icons')} >
              { displayOptions.includes('facebook') && facebookUrl &&
                this.generateSocialMediaIcon('facebookFilled', facebookUrl, 'facebook') }
              { displayOptions.includes('linkedIn') && linkedInUrl &&
                this.generateSocialMediaIcon('linkedInFilled', linkedInUrl, 'linkedIn') }
              { displayOptions.includes('twitter') && twitterUrl &&
                this.generateSocialMediaIcon('twitterFilled', twitterUrl, 'twitter') }
            </div>
            <InteractiveElement
              {...resolve(props, 'speakerModalExit', 'speakerModalExitIcon')}
              {...tapOrClick(() => showBioModal())}
              {...resolveTestId(props, '-close-button')}
            >
              <Icon icon="closeDeleteFilled" />
            </InteractiveElement>
          </div>
        </div>
        <div {...resolve(props, 'speakerModalBiography')} {...resolveTestId(props, '-modal-biography')} >
          { displayOptions.includes('biography') && <span>{ translate(biography) } </span> }
        </div>
        <div
          {...resolve(props, 'websitesSeparation')}
          {...resolveTestId(props, '-modal-documents-websites-container')}
        >
          { displayOptions.includes('speakerDocuments') &&
            <SpeakerDocuments
              speakerId={id}
              classes={classes}
              style={style}
              translate={translate}
              getSpeakerDocuments={getSpeakerDocuments}
              onDocumentDownload={this.context.recordSpeakerFileDownloadActivity}
            />
          }
          { displayOptions.includes('websites') && this.hasWebsites(visibleWebsites) &&
            <div
              {...resolve({ props, classes: props.classes }, 'speakerWebsitesContainer')}
              {...resolveTestId(props, '-modal-websites')}>
              <div {...resolve(props, 'speakerModalWebsitesTitle')}>
                {translate('EventWidgets_Speakers_Modal_Websites__resx')}
              </div>
              <ul {...resolve(props, 'speakerLinks')}>
                {[
                  this.renderWebsites(visibleWebsites)
                ]}
              </ul>
            </div>
          }
        </div>
      </Dialog>
    );
  }
}
